import { history } from 'store/store';
import { useHttpStatus } from 'hooks/';
import {
  StyledWrapper,
  StyledButtonWrapper,
  StyledButtonContainer,
} from 'components/Item/Form/Form.styled';
import { active } from 'constants/status';
import { useParams } from 'react-router-dom';
import { Detail, General } from 'components/Item/';
import { UPDATE_ITEM, CREATE_ITEM } from 'actions/types';
import { ARCHIVED, PERISHABLE } from 'constants/inventory';
import { ReactComponent as TickIcon } from 'icons/tick.svg';
import React, { useState, useEffect, useMemo } from 'react';
import { INVALID_URL, INVENTORY_PATH } from 'constants/general';
import { ReactComponent as EditIcon } from 'icons/edit_circle.svg';
import { parseJSON, compareDate, capitalizeFirstLetter } from 'utils/';
import { arrayOf, bool, func, number, object, string } from 'prop-types';
import { RoundButton, CancelButton, DeleteButton } from 'components/Buttons/';

const invalidData = ['', 'null', null, undefined, 'undefined'];

/**
 * @param {{
 * tags : []
 * good : {}
 * items : []
 * upload : []
 * categories : []
 * warehouses : []
 * quantityUnits : []
 * onSubmit : function
 * isLoading : boolean
 * showAddUI : boolean
 * maxQuantity : number
 * hasCondition : boolean
 * setShowAddUI : function
 * onShowQuantityModal : function
 * }} param
 */
function Form({
  tags,
  good,
  items,
  upload,
  onSubmit,
  itemName,
  warehouses,
  categories,
  maxQuantity,
  isOnBoarding,
  setShowAddUI,
  hasCondition,
  quantityUnits,
  onShowQuantityModal,
}) {
  const { id } = useParams();
  const { response: createdItem } = useHttpStatus({
    actions: [CREATE_ITEM, UPDATE_ITEM],
  });
  const [isUpLoaded, setIsUpLoaded] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const item = useMemo(() => {
    if (!id || (!items.length && !upload.length)) {
      return {};
    }
    const saved = items.find(function (item) {
      return item.id === id;
    });
    if (saved) {
      return saved;
    }
    const uploaded = upload.find(function (item) {
      return item.id === id;
    });
    if (uploaded) {
      setIsUpLoaded(true);
      return uploaded;
    }
    return {};
  }, [id, items, upload]);

  const ordering = useMemo(() => {
    if (item && good) {
      const { attributes, conditions } = item;
      if (attributes && attributes.length > 0) {
        const active = attributes.filter(function ({ quantity, condition }) {
          return quantity > 0 && condition === good?.id;
        });
        return active.length;
      }
      if (conditions) {
        const obj = conditions.conditions;
        const entries = Object.entries(obj);
        const active = entries.filter(function ([key, value]) {
          return value > 0 && key === good?.id;
        });
        return active.length > 0 ? active[0][1] : 0;
      }
    }
    return item?.quantity || 0;
  }, [item, good]);

  const {
    sku = '',
    quantity = '',
    status = active,
    manufacturer = '',
    item_tags: itemTags,
    item_name: name = '',
    item_images: itemImages,
    item_type: itemType = '',
    expiry_date: expiryDate = '',
    price_unit: priceUnit = 'KES',
    warehouse_id: warehouseId = '',
    item_category: itemCategory = '',
    purchase_date: purchaseDate = '',
    leasing_price: leasingPrice = '',
    other_details: otherDetails = '',
    quantity_unit: quantityUnit = '',
    item_description: description = '',
    purchase_price: purchasePrice = '',
    item_order_type: itemOrderType = '',
    warranty_expiry: warrantyExpiry = '',
  } = item;
  let itemTag = null;
  const currentQuantityUnit = !quantityUnit
    ? null
    : {
        label: quantityUnit,
        value: quantityUnit,
      };
  if (Array.isArray(itemTags)) {
    itemTag = itemTags[0];
  }
  const otherDetailsObj = parseJSON(otherDetails);
  const { sellPrice = '', productionDate = '' } = otherDetailsObj;

  const sateObject = {
    sku: unescape(sku),
    name: unescape(name),
    tag: unescape(itemTag),
    status: unescape(status),
    quantity: unescape(quantity),
    itemType: unescape(itemType),
    sellPrice: unescape(sellPrice),
    priceUnit: unescape(priceUnit),
    warehouse: unescape(warehouseId),
    category: unescape(itemCategory),
    description: unescape(description),
    manufacturer: unescape(manufacturer ?? ''),
    leasingPrice: unescape(leasingPrice),
    quantityUnit: unescape(quantityUnit),
    purchasePrice: unescape(purchasePrice),
    itemOrderType: unescape(itemOrderType),
    // dates
    expiryDate: expiryDate ? new Date(expiryDate) : '',
    purchaseDate: purchaseDate ? new Date(purchaseDate) : '',
    warrantyExpiry: warrantyExpiry ? new Date(warrantyExpiry) : '',
    productionDate: productionDate ? new Date(productionDate) : '',
  };

  const [errors, setErrors] = useState({
    tag: '',
    name: '',
    itemType: '',
    category: '',
    quantity: '',
    warehouse: '',
    priceUnit: '',
    sellPrice: '',
    expiryDate: '',
    description: '',
    manufacturer: '',
    purchaseDate: '',
    leasingPrice: '',
    quantityUnit: '',
    purchasePrice: '',
    itemOrderType: '',
    warrantyExpiry: '',
    productionDate: '',
  });
  const [state, setState] = useState(sateObject);
  const [itemLogo, setItemLogo] = useState(null);
  const [isReadOnly, setIsReadOnly] = useState(!!id);
  const isPerishable = state.itemType === PERISHABLE;
  const [showDetails, setShowDetails] = useState(false);

  /**
   * @param {{
   * currentTarget: {
   * name: string
   * value: string
   * }
   * }} event
   */
  function onInputChange(event) {
    const { currentTarget } = event;
    const { name, value } = currentTarget;
    if (name === 'quantity' && +value > maxQuantity) {
      setErrors({
        ...errors,
        quantity: `Quantity should be less than ${maxQuantity}`,
      });
      return;
    }
    setErrors({
      ...errors,
      quantity: '',
    });
    setState({
      ...state,
      [name]: value,
    });
  }

  /**
   * @param {Date} date
   * @param {string} name
   */
  function onDateChange(name, date) {
    setState({
      ...state,
      [name]: date,
    });
  }

  function onGotToInventory() {
    if (!isOnBoarding) {
      history.push(INVENTORY_PATH);
    } else {
      setShowAddUI(false);
    }
  }

  function onEnableEditing() {
    setIsReadOnly(false);
  }

  /**
   * @param {boolean} isDeleting
   */
  function onSubmitItem(isDeleting) {
    setIsSubmitting(true);
    const isFormValid = validateItemForm();
    if (isFormValid) {
      const {
        sku,
        tag,
        name,
        itemType,
        category,
        quantity,
        warehouse,
        priceUnit,
        sellPrice,
        expiryDate,
        description,
        leasingPrice,
        manufacturer,
        purchaseDate,
        quantityUnit,
        itemOrderType,
        purchasePrice,
        warrantyExpiry,
        productionDate,
      } = state;
      const otherDetails = {
        sellPrice,
      };
      const formData = new FormData();
      if (id) {
        formData.append('id', id);
      }
      if (!invalidData.includes(sku)) {
        formData.append('sku', sku);
      }
      const newStatus = isDeleting ? ARCHIVED : status;
      formData.append('item_name', name);
      formData.append('status', newStatus);
      formData.append('quantity', quantity);
      formData.append('item_type', itemType);
      formData.append('price_unit', priceUnit);
      formData.append('warehouse_id', warehouse);
      formData.append('item_category', category);
      formData.append('manufacturer', manufacturer);
      formData.append('quantity_unit', quantityUnit);
      if (!invalidData.includes(leasingPrice)) {
        formData.append('leasing_price', leasingPrice);
      }
      formData.append('item_description', description);
      formData.append('item_order_type', itemOrderType);
      if (!invalidData.includes(purchasePrice)) {
        formData.append('purchase_price', purchasePrice);
      }
      if (!invalidData.includes(tag)) {
        formData.append('item_tags', JSON.stringify([tag]));
      }
      if (!invalidData.includes(itemLogo)) {
        formData.append('item_images', itemLogo);
      }
      // dates
      if (isDateObject(expiryDate)) {
        formData.append('expiry_date', new Date(expiryDate).toISOString());
      }
      if (isDateObject(warrantyExpiry)) {
        formData.append(
          'warranty_expiry',
          new Date(warrantyExpiry).toISOString(),
        );
      }
      if (isDateObject(purchaseDate)) {
        formData.append('purchase_date', new Date(purchaseDate).toISOString());
      }
      if (isDateObject(productionDate)) {
        otherDetails.productionDate = new Date(productionDate).toISOString();
      }
      // Other details
      formData.append('other_details', JSON.stringify(otherDetails));
      onSubmit({
        payload: formData,
        uploaded: isUpLoaded,
        deleting: isDeleting,
      });
    }
  }

  /**
   * @param {Date | undefined} date
   */
  function isDateObject(date) {
    return date && typeof date === 'object';
  }

  function validateItemForm() {
    const {
      itemType,
      expiryDate,
      purchaseDate,
      warrantyExpiry,
      productionDate,
    } = state;
    let isFormValid = true;
    const optionalFields = [
      'tag',
      'sku',
      'itemTags',
      'sellPrice',
      'expiryDate',
      'description',
      'purchaseDate',
      'leasingPrice',
      'manufacturer',
      'warrantyExpiry',
      'productionDate',
    ];
    let errorObject = { ...errors };
    for (const [key, value] of Object.entries(state)) {
      if (!optionalFields.includes(key) && !value) {
        isFormValid = false;
        errorObject = {
          ...errorObject,
          [key]: `Incorrect ${capitalizeFirstLetter(key, true)}`,
        };
      } else {
        errorObject = {
          ...errorObject,
          [key]: '',
        };
      }
    }
    if (purchaseDate && warrantyExpiry) {
      if (compareDate(purchaseDate, warrantyExpiry, '>=')) {
        isFormValid = false;
        errorObject = {
          ...errorObject,
          warrantyExpiry: 'Warranty expiry should be after purchase date',
        };
        setShowDetails(true);
      } else {
        errorObject = {
          ...errorObject,
          warrantyExpiry: '',
        };
      }
    }
    if (itemType === PERISHABLE && !expiryDate) {
      isFormValid = false;
      errorObject = {
        ...errorObject,
        expiryDate: 'Incorrect Expiry Date',
      };
    } else {
      errorObject = {
        ...errorObject,
        expiryDate: '',
      };
    }
    if (isPerishable && expiryDate && productionDate) {
      if (compareDate(expiryDate, productionDate, '<=')) {
        isFormValid = false;
        errorObject = {
          ...errorObject,
          expiryDate: 'Expiry date should be after production date',
        };
        setShowDetails(true);
      } else {
        errorObject = {
          ...errorObject,
          expiryDate: '',
        };
      }
    }
    setErrors(errorObject);
    return isFormValid;
  }

  useEffect(() => {
    if (isSubmitting) {
      validateItemForm();
    }
  }, [state, isSubmitting]);

  useEffect(() => {
    if (createdItem && createdItem.id) {
      if (
        createdItem.updatedValues &&
        createdItem.updatedValues.status === ARCHIVED
      ) {
        onGotToInventory();
      } else {
        setIsReadOnly(true);
      }
    }
  }, [createdItem]);

  useEffect(() => {
    setState(sateObject);
    let photoUrl = null;
    if (Array.isArray(itemImages)) {
      photoUrl = itemImages[0].link;
    }
    //  TODO : This was for cleanup purposes, remove in future ?
    if (typeof itemImages === 'string' && !INVALID_URL.includes(itemImages)) {
      photoUrl = itemImages;
    }
    setItemLogo(photoUrl);
  }, [item]);

  return (
    <StyledWrapper>
      <General
        id={id}
        item={item}
        state={state}
        errors={errors}
        setState={setState}
        itemLogo={itemLogo}
        itemName={itemName}
        ordering={ordering}
        isReadOnly={isReadOnly}
        categories={categories}
        warehouses={warehouses}
        setItemLogo={setItemLogo}
        warehouseId={warehouseId}
        showDetails={showDetails}
        maxQuantity={maxQuantity}
        hasCondition={hasCondition}
        isOnBoarding={isOnBoarding}
        itemCategory={itemCategory}
        onDateChange={onDateChange}
        attributes={item.attributes}
        onInputChange={onInputChange}
        quantityUnits={quantityUnits}
        setShowDetails={setShowDetails}
        onShowQuantityModal={onShowQuantityModal}
        currentQuantityUnit={currentQuantityUnit}
      />
      {!showDetails ? null : (
        <Detail
          tags={tags}
          state={state}
          itemSKU={sku}
          errors={errors}
          itemTag={itemTag}
          setState={setState}
          isReadOnly={isReadOnly}
          onDateChange={onDateChange}
          onInputChange={onInputChange}
        />
      )}
      <StyledButtonContainer>
        {isReadOnly ? (
          <StyledButtonWrapper>
            <RoundButton
              caption="Edit"
              flexWidth={100}
              icon={<EditIcon />}
              onClick={onEnableEditing}
            />
          </StyledButtonWrapper>
        ) : (
          <StyledButtonWrapper>
            <RoundButton
              caption="Save"
              flexWidth={100}
              icon={<TickIcon />}
              onClick={onSubmitItem.bind(null, false)}
            />
          </StyledButtonWrapper>
        )}
        <StyledButtonWrapper>
          <CancelButton onClick={onGotToInventory} />
        </StyledButtonWrapper>
        {id && item?.status !== ARCHIVED && !isUpLoaded ? (
          <StyledButtonWrapper>
            <DeleteButton onClick={onSubmitItem.bind(null, true)} />
          </StyledButtonWrapper>
        ) : null}
      </StyledButtonContainer>
    </StyledWrapper>
  );
}

Form.propTypes = {
  onSubmit: func,
  showAddUI: bool,
  setShowAddUI: func,
  maxQuantity: number,
  tags: arrayOf(object),
  items: arrayOf(object),
  upload: arrayOf(object),
  warehouses: arrayOf(object),
  categories: arrayOf(object),
  quantityUnits: arrayOf(string),
};

export default Form;
